import * as React from 'react';
import { useEffect, useRef } from 'react';

import CreditsLimitBanner from '@repo/editor/src/components/CreditsLimitBanner/CreditsLimitBanner';
import { FREE_PLAN_SINGLE_USE_CREDITS_AMOUNT } from '@repo/editor/src/global/pricingConfig';
import {
  authenticationStoreSelector,
  useAuthenticationStore,
} from '@repo/editor/src/stores/authenticationStore';
import {
  promotionSelector,
  usePromotionStore,
} from '@repo/editor/src/stores/promotionStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import {
  Button,
  primarySmallTheme,
  primaryTheme,
} from '@repo/ui/components/Buttons';
import GiftIcon from '@repo/ui/components/Icon/GiftIcon';
import { themeStyle } from '@repo/ui/theming';

import { UpgradeNotificationBoxProps } from 'components/AiTools/UpgradeNotificatioBox/types';
import { FlexRow } from 'styles/layouts';
import { LayoutSwitch } from 'styles/queries';

import { NotifyBox } from '../../utilities/pageStyles';

import { NotifyText } from './styles';

const UpgradeNotificationBox: React.FC<UpgradeNotificationBoxProps> = ({
  displayIn,
  upgradeModalType,
}) => {
  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );
  const user = useUserStore(userStoreSelector.user);
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );

  const initializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (
      !user.creditInfo?.extraTotal &&
      !user.creditInfo?.recurringRemaining &&
      !initializedRef.current &&
      user.verificationCreditsGranted
    ) {
      showUpgradeModal(upgradeModalType);
    }
    // we want to show it only on first load
    initializedRef.current = true;
  }, [user, showUpgradeModal, upgradeModalType]);

  if (!user) {
    const handleOnSignUp = (): void => {
      openAuthentication({
        blurBackground: true,
      });
    };

    return (
      <NotifyBox padding="20px">
        <FlexRow alignItems="center" justifyContent="space-between">
          <FlexRow alignItems="center" gap="8px">
            <GiftIcon height="24px" theme={{ color: themeStyle.varBlueMain }} />
            <NotifyText>
              Sign up to get {FREE_PLAN_SINGLE_USE_CREDITS_AMOUNT} free credits.
            </NotifyText>
          </FlexRow>
          <LayoutSwitch
            desktopProps={{
              theme: primaryTheme,
              width: '145px',
            }}
            mobileProps={{
              theme: primarySmallTheme,
              width: 'fit-content',
              height: '30px',
            }}
          >
            <Button
              label="Get Free Credits"
              name="getFreeCreditsButton"
              style={{ whiteSpace: 'nowrap' }}
              onClick={handleOnSignUp}
            />
          </LayoutSwitch>
        </FlexRow>
      </NotifyBox>
    );
  }

  return <CreditsLimitBanner displayIn={displayIn} />;
};

export default withEventScope(UpgradeNotificationBox, {
  module: 'upgradeNotification',
});
